import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { SharedFields, teamMembersSectionConfig } from "../../configs";
import {
  AccordionSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";
import { milestonesSectionConfig } from "./milestones-config";

export const sourceOfBudgetField: ScreenSectionConfigType = {
  field: "sourceOfBudget",
  label: "national_projects.external_budget_source",
  view: "normal",
};

export const contributingEntitiesField: ListSectionConfigType = {
  field: "contributingEntities",
  label: "kpi.contributing_entities",
  view: "list",
  idKey: "entityId",
  derivedList: (data: unknown) => {
    const list = data as { otherEntityId?: number }[];
    return list.filter(($i) => !AdaaHelper.isDefined($i?.otherEntityId));
  },
  format: ($o) => AdaaHelper.getItemValueByToken($o, "entityName"),
};

export const otherContributingEntitiesField: ListSectionConfigType = {
  field: "contributingEntities",
  label: "other_entities.contrib_entities",
  view: "list",
  idKey: "otherEntityId",
  derivedList: (data: unknown) => {
    const list = data as { otherEntityId?: number }[];
    return list.filter(($i) => AdaaHelper.isDefined($i?.otherEntityId));
  },
  format: ($o) => AdaaHelper.getItemValueByToken($o, "otherEntityName"),
};

export const transformationalTargetsField: ListSectionConfigType = {
  field: "transformationalTargets",
  label: "national_projects.transformational_targets.title",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
};

export const stoppedStrategicProjectsField: ListSectionConfigType = {
  field: "stoppedStrategicProjects",
  label: "national_projects.stop_strategic_projects",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "initiativeName"),
};

export const decisionsField: ListSectionConfigType = {
  field: "decisions",
  label: "national_projects.pmo_decisions",
  view: "list",
  idKey: "optionId",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format: ($o: any) => {
    let value = AdaaHelper.getItemValueByToken($o, "option");
    if ($o.optionId === Constants.CONSTANT_PMO_DECISION_OTHERS_ID) {
      value += ` (${$o.comment})`;
    }
    return value;
  },
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as { optionId?: number; comment?: string };
    const $o = $oldValue as { optionId?: number; comment?: string };
    if (
      $n.optionId === Constants.CONSTANT_PMO_DECISION_OTHERS_ID &&
      $o.optionId === Constants.CONSTANT_PMO_DECISION_OTHERS_ID
    ) {
      return $n.comment !== $o.comment;
    }

    return $n.optionId !== $o.optionId;
  },
};

export const targetAudienceField: ListSectionConfigType = {
  field: "targetAudience",
  label: "national_projects.target_audience",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "audience"),
};

export const expectedImpactField: ListSectionConfigType = {
  field: "expectedImpact",
  label: "national_projects.expected_impact",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "impact"),
};

export const commentsField: ListSectionConfigType = {
  field: "comments",
  label: "national_projects.comments",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "comments"),
};

export const projectMilestonesField: AccordionSectionConfigType = {
  view: "accordion",
  field: "milestones",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: milestonesSectionConfig,
  label: "national_projects.national_projects_milestone.title",
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as Record<string, unknown>;
    const $o = $oldValue as Record<string, unknown>;
    const keys = [
      "id",
      "nameAE",
      "nameEN",
      "dscAE",
      "dscAE",
      "startDate",
      "endDate",
      "entityId",
      "otherEntityId",
      "owner",
      "budget",
      "milestoneOrder",
    ];

    return keys.some((key) => $o[key] !== $n[key]);
  },
};

export const projectTeamMembersField: AccordionSectionConfigType = {
  view: "accordion",
  field: "projectTeamMembers",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: teamMembersSectionConfig,
  label: "national_projects.project_team_members.popup_title",
};

export const linkedNationalStrategiesField: ScreenSectionConfigType = {
  view: "linked-national-strategies",
  field: "govDirections",
  label: "national_strategies.strategies",
};

export const commonTpScreenConfig = {
  type: "kpi" as ScreenType,
  sections: [
    ...SharedFields.name,
    ...SharedFields.description,
    {
      ...SharedFields.objectiveIdField,
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "objectiveName"),
    },
    {
      ...SharedFields.startDate,
      label: "national_projects.planned_start_date",
    },
    {
      ...SharedFields.endDate,
      label: "national_projects.planned_end_date",
    },
    {
      ...SharedFields.entityIdField,
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "accountableEntityName"),
      label: "national_projects.accountable_entity",
    },
    {
      ...SharedFields.entityIdField,
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "otherEntityName"),
      label: "national_projects.other_accountable_entity",
    },
    SharedFields.orgUnitIdField,
    {
      ...SharedFields.ownerField,
      label: "national_projects.entity_lead",
    },
    {
      ...SharedFields.ownerField,
      field: "projectCoordinator",
      label: "national_projects.project_coordinator",
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "projectCoordinatorName"),
    },
    sourceOfBudgetField,
    contributingEntitiesField,
    otherContributingEntitiesField,
    transformationalTargetsField,
    SharedFields.nationalSectorsField,
    stoppedStrategicProjectsField,
    decisionsField,
    targetAudienceField,
    expectedImpactField,
    commentsField,
    linkedNationalStrategiesField,
    projectMilestonesField,
    projectTeamMembersField,
    SharedFields.attachmentsField,
  ],
};
